import type { Location } from 'react-router-dom'

const PREFIXES = ['/search', '/discover']
const matchesPrefix = (l: Location) =>
  PREFIXES.some(p => l?.pathname?.startsWith(p))

/**
 * Determines the best return location when closing a modal.
 *
 * Prioritizes search and discover pages over other locations, as users typically want to return to their previous results.
 * Falls back to the provided fallback location (defaults to home page).
 *
 * @param currentLocation - The current location where the modal is open
 * @param previousLocation - The location before the modal was opened
 * @param fallback - Fallback location if neither current nor previous are search pages
 * @returns The best location to return to when closing the modal
 *
 * @example
 * ```typescript
 * const current = { pathname: '/track/123' }
 * const previous = { pathname: '/search?q=rock' }
 * const fallback = { pathname: '/library' }
 *
 * const returnTo = getModalBestReturnTo(current, previous, fallback)
 * // Returns previous location: { pathname: '/search?q=rock' }
 *
 * // If neither is a search page
 * const current2 = { pathname: '/track/123' }
 * const previous2 = { pathname: '/library' }
 * const returnTo2 = getModalBestReturnTo(current2, previous2, fallback)
 * // Returns fallback: { pathname: '/library' }
 * ```
 */
export const getModalBestReturnTo = (
  currentLocation: Location,
  previousLocation: Location,
  fallback: Location = {
    pathname: '/',
    state: {},
    key: '',
    search: '',
    hash: '',
  },
): Location => {
  if (matchesPrefix(previousLocation)) return previousLocation
  if (matchesPrefix(currentLocation)) return currentLocation

  return fallback
}
